/**
 * Theme application — two themes, light and dark.
 *
 * Was five. Dim, Forest and Sepia were deleted on 14 September 2026: they cost
 * 90 hand-tuned variable overrides across three palettes (45 / 30 / 15 — the
 * unevenness is the tell) that no user could select, because the picker had
 * been narrowed to light + dark and never widened back.
 *
 * The theme axis was also doing the accent's job. Someone reaching for Sepia
 * wanted a warmer product, not a warmer ground; that belongs to a selectable
 * accent on two verified grounds, not to a fourth hand-tuned theme.
 *
 * Design: lilia-docs/design-handoffs/2026-09-14-color-system/
 */

export const THEME_NAMES = ['light', 'dark'] as const
export type ThemeName = (typeof THEME_NAMES)[number]

export const DARK_FAMILY: ThemeName[] = ['dark']

/**
 * Classes the deleted themes used to put on <html>. Kept only so a browser
 * holding one from before this change is cleaned on the next apply — the
 * stylesheets backing them are gone, so a stale class would otherwise sit
 * there inert but confusing in the inspector. Safe to drop once no session
 * predates September 2026.
 */
const LEGACY_VARIANT_CLASSES = ['theme-dim', 'theme-forest', 'theme-sepia']

/** Map a persisted name — including a deleted one — onto a live theme. */
export function normalizeThemeName(name: unknown): ThemeName {
    if (name === 'light' || name === 'dark') return name
    // dim was derived from dark; forest and sepia from light.
    if (name === 'dim') return 'dark'
    if (name === 'forest' || name === 'sepia') return 'light'
    if (typeof window !== 'undefined' && window.matchMedia) {
        return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
    }
    return 'light'
}

export function modeForTheme(name: ThemeName): 'light' | 'dark' {
    return DARK_FAMILY.includes(name) ? 'dark' : 'light'
}

/** Clear any pending theming timeout so rapid toggles don’t stack. */
let themingTimer: ReturnType<typeof setTimeout> | null = null

/**
 * Apply the theme to <html>. `.dark` is owned by useDarkMode from `mode`;
 * this now only clears the deleted variants and runs the cross-fade.
 */
export function applyThemeVariant(_name: ThemeName, withTransition = true): void {
    if (typeof document === 'undefined') return
    const root = document.documentElement

    LEGACY_VARIANT_CLASSES.forEach((c) => root.classList.remove(c))

    if (withTransition) {
        if (themingTimer != null) {
            window.clearTimeout(themingTimer)
            themingTimer = null
        }
        root.classList.add('theming')
        themingTimer = window.setTimeout(() => {
            root.classList.remove('theming')
            themingTimer = null
        }, 160)
    }
}

export function nextTheme(current: ThemeName): ThemeName {
    const i = THEME_NAMES.indexOf(normalizeThemeName(current))
    return THEME_NAMES[(i + 1) % THEME_NAMES.length]
}

export const THEME_LABELS: Record<ThemeName, string> = {
    light: 'Light',
    dark: 'Dark',
}
